// =====================================================
// CATALOG TREE - Árbol principal del catálogo con búsqueda
// =====================================================

import { useState, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import { CatalogTreeItem } from './CatalogTreeItem';
import { LinkPreviewDialog } from './LinkPreviewDialog';
import { UnlinkPreviewDialog } from './UnlinkPreviewDialog';
import { catalogApi } from '../../api/catalog';
import { toast } from '../ui/Toast';
import type {
  CatalogSpecialty,
  CatalogSubspecialty,
  CatalogTreatmentGroup,
  CatalogAct,
  CatalogItemType,
  TreeExpandedState,
  UnlinkPreviewResponse,
} from '../../types/catalog';

interface CatalogTreeProps {
  siteId: string;
  specialties: CatalogSpecialty[];
  onRefresh: () => void;
}

interface ParentInfo {
  type: CatalogItemType;
  name: string;
  id: string;
}

// Función para normalizar texto (quitar acentos y lowercase)
const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

// Función para verificar si un texto coincide con la búsqueda
const matchesSearch = (text: string, search: string): boolean => {
  if (!search) return true;
  return normalizeText(text).includes(normalizeText(search));
};

export function CatalogTree({ siteId, specialties, onRefresh }: CatalogTreeProps) {
  // Estado de búsqueda
  const [searchQuery, setSearchQuery] = useState('');
  
  // Estado de expansión
  const [expanded, setExpanded] = useState<TreeExpandedState>({
    specialties: {},
    subspecialties: {},
    treatmentGroups: {},
  });

  // Estado de carga por item (setLoadingItems reservado para uso futuro)
  const [loadingItems] = useState<Set<string>>(new Set());

  // Estado del diálogo de link
  const [linkDialog, setLinkDialog] = useState<{
    isOpen: boolean;
    isLinking: boolean;
    itemId: string;
    itemName: string;
    itemType: CatalogItemType;
    parentsToLink: ParentInfo[];
  }>({
    isOpen: false,
    isLinking: false,
    itemId: '',
    itemName: '',
    itemType: 'specialty',
    parentsToLink: [],
  });

  // Estado del diálogo de unlink
  const [unlinkDialog, setUnlinkDialog] = useState<{
    isOpen: boolean;
    isLoading: boolean;
    isUnlinking: boolean;
    itemId: string;
    itemType: CatalogItemType;
    preview: UnlinkPreviewResponse | null;
  }>({
    isOpen: false,
    isLoading: false,
    isUnlinking: false,
    itemId: '',
    itemType: 'specialty',
    preview: null,
  });

  // Crear un índice para búsqueda rápida de padres
  const catalogIndex = useMemo(() => {
    const index: {
      subspecialties: Map<string, { specialty: CatalogSpecialty }>;
      treatmentGroups: Map<string, { specialty: CatalogSpecialty; subspecialty: CatalogSubspecialty }>;
      medicalActs: Map<string, { specialty: CatalogSpecialty; subspecialty: CatalogSubspecialty; treatmentGroup: CatalogTreatmentGroup }>;
    } = {
      subspecialties: new Map(),
      treatmentGroups: new Map(),
      medicalActs: new Map(),
    };

    specialties.forEach((specialty) => {
      specialty.subspecialties.forEach((subspecialty) => {
        index.subspecialties.set(subspecialty.id, { specialty });
        
        subspecialty.treatmentGroups.forEach((group) => {
          index.treatmentGroups.set(group.id, { specialty, subspecialty });
          
          group.acts.forEach((act) => {
            index.medicalActs.set(act.id, { specialty, subspecialty, treatmentGroup: group });
          });
        });
      });
    });

    return index;
  }, [specialties]);

  // Filtrar y calcular qué elementos mostrar basándose en la búsqueda
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) {
      return { specialties, matchedIds: new Set<string>(), expandedBySearch: {} as TreeExpandedState };
    }

    const matchedIds = new Set<string>();
    const expandedBySearch: TreeExpandedState = {
      specialties: {},
      subspecialties: {},
      treatmentGroups: {},
    };

    // Función para verificar si un acto coincide
    const actMatches = (act: CatalogAct): boolean => {
      return matchesSearch(act.name, searchQuery) || matchesSearch(act.code, searchQuery);
    };

    // Función para verificar si un grupo coincide (directamente o por sus actos)
    const groupMatches = (group: CatalogTreatmentGroup): { matches: boolean; hasMatchingChildren: boolean } => {
      const directMatch = matchesSearch(group.name, searchQuery) || matchesSearch(group.code, searchQuery);
      const hasMatchingActs = group.acts.some(actMatches);
      return { matches: directMatch || hasMatchingActs, hasMatchingChildren: hasMatchingActs };
    };

    // Función para verificar si una subespecialidad coincide
    const subspecialtyMatches = (sub: CatalogSubspecialty): { matches: boolean; hasMatchingChildren: boolean } => {
      const directMatch = matchesSearch(sub.name, searchQuery) || matchesSearch(sub.code, searchQuery);
      const hasMatchingGroups = sub.treatmentGroups.some(g => groupMatches(g).matches);
      return { matches: directMatch || hasMatchingGroups, hasMatchingChildren: hasMatchingGroups };
    };

    // Función para verificar si una especialidad coincide
    const specialtyMatches = (spec: CatalogSpecialty): { matches: boolean; hasMatchingChildren: boolean } => {
      const directMatch = matchesSearch(spec.name, searchQuery) || matchesSearch(spec.code, searchQuery);
      const hasMatchingSubs = spec.subspecialties.some(s => subspecialtyMatches(s).matches);
      return { matches: directMatch || hasMatchingSubs, hasMatchingChildren: hasMatchingSubs };
    };

    // Filtrar especialidades
    const filtered = specialties.filter(spec => {
      const result = specialtyMatches(spec);
      if (result.matches) {
        if (result.hasMatchingChildren) {
          expandedBySearch.specialties[spec.id] = true;
        }
        
        // Verificar subespecialidades
        spec.subspecialties.forEach(sub => {
          const subResult = subspecialtyMatches(sub);
          if (subResult.matches) {
            if (subResult.hasMatchingChildren) {
              expandedBySearch.subspecialties[sub.id] = true;
            }
            
            // Verificar grupos
            sub.treatmentGroups.forEach(group => {
              const groupResult = groupMatches(group);
              if (groupResult.matches) {
                if (groupResult.hasMatchingChildren) {
                  expandedBySearch.treatmentGroups[group.id] = true;
                }
                
                // Marcar actos que coinciden
                group.acts.forEach(act => {
                  if (actMatches(act)) {
                    matchedIds.add(act.id);
                  }
                });

                // Si el grupo coincide directamente, marcarlo
                if (matchesSearch(group.name, searchQuery) || matchesSearch(group.code, searchQuery)) {
                  matchedIds.add(group.id);
                }
              }
            });

            // Si la subespecialidad coincide directamente, marcarla
            if (matchesSearch(sub.name, searchQuery) || matchesSearch(sub.code, searchQuery)) {
              matchedIds.add(sub.id);
            }
          }
        });

        // Si la especialidad coincide directamente, marcarla
        if (matchesSearch(spec.name, searchQuery) || matchesSearch(spec.code, searchQuery)) {
          matchedIds.add(spec.id);
        }
      }
      return result.matches;
    });

    return { specialties: filtered, matchedIds, expandedBySearch };
  }, [specialties, searchQuery]);

  // Combinar expansión manual con expansión por búsqueda
  const effectiveExpanded = useMemo(() => {
    if (!searchQuery.trim()) {
      return expanded;
    }
    return {
      specialties: { ...expanded.specialties, ...filteredData.expandedBySearch.specialties },
      subspecialties: { ...expanded.subspecialties, ...filteredData.expandedBySearch.subspecialties },
      treatmentGroups: { ...expanded.treatmentGroups, ...filteredData.expandedBySearch.treatmentGroups },
    };
  }, [expanded, searchQuery, filteredData.expandedBySearch]);

  // Calcular padres no vinculados
  const getUnlinkedParents = useCallback((id: string, type: CatalogItemType): ParentInfo[] => {
    const parents: ParentInfo[] = [];

    if (type === 'subspecialty') {
      const data = catalogIndex.subspecialties.get(id);
      if (data && !data.specialty.isLinked) {
        parents.push({ type: 'specialty', name: data.specialty.name, id: data.specialty.id });
      }
    } else if (type === 'treatmentGroup') {
      const data = catalogIndex.treatmentGroups.get(id);
      if (data) {
        if (!data.specialty.isLinked) {
          parents.push({ type: 'specialty', name: data.specialty.name, id: data.specialty.id });
        }
        if (!data.subspecialty.isLinked) {
          parents.push({ type: 'subspecialty', name: data.subspecialty.name, id: data.subspecialty.id });
        }
      }
    } else if (type === 'medicalAct') {
      const data = catalogIndex.medicalActs.get(id);
      if (data) {
        if (!data.specialty.isLinked) {
          parents.push({ type: 'specialty', name: data.specialty.name, id: data.specialty.id });
        }
        if (!data.subspecialty.isLinked) {
          parents.push({ type: 'subspecialty', name: data.subspecialty.name, id: data.subspecialty.id });
        }
        if (!data.treatmentGroup.isLinked) {
          parents.push({ type: 'treatmentGroup', name: data.treatmentGroup.name, id: data.treatmentGroup.id });
        }
      }
    }

    return parents;
  }, [catalogIndex]);

  // Obtener nombre del item
  const getItemName = useCallback((id: string, type: CatalogItemType): string => {
    if (type === 'specialty') {
      return specialties.find(s => s.id === id)?.name || '';
    } else if (type === 'subspecialty') {
      const data = catalogIndex.subspecialties.get(id);
      return data ? specialties.find(s => s.id === data.specialty.id)?.subspecialties.find(sub => sub.id === id)?.name || '' : '';
    } else if (type === 'treatmentGroup') {
      const data = catalogIndex.treatmentGroups.get(id);
      return data?.subspecialty.treatmentGroups.find((g) => g.id === id)?.name || '';
    } else if (type === 'medicalAct') {
      const data = catalogIndex.medicalActs.get(id);
      return data?.treatmentGroup.acts.find((a) => a.id === id)?.name || '';
    }
    return '';
  }, [specialties, catalogIndex]);

  // Toggle expansión
  const toggleExpand = useCallback((type: keyof TreeExpandedState, id: string) => {
    setExpanded((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [id]: !prev[type][id],
      },
    }));
  }, []);

  // Expandir/colapsar todo
  const expandAll = useCallback(() => {
    const newExpanded: TreeExpandedState = {
      specialties: {},
      subspecialties: {},
      treatmentGroups: {},
    };

    filteredData.specialties.forEach((spec) => {
      newExpanded.specialties[spec.id] = true;
      spec.subspecialties.forEach((sub) => {
        newExpanded.subspecialties[sub.id] = true;
        sub.treatmentGroups.forEach((group) => {
          newExpanded.treatmentGroups[group.id] = true;
        });
      });
    });

    setExpanded(newExpanded);
  }, [filteredData.specialties]);

  const collapseAll = useCallback(() => {
    setExpanded({
      specialties: {},
      subspecialties: {},
      treatmentGroups: {},
    });
  }, []);

  // Limpiar búsqueda
  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Manejar toggle de vinculación
  const handleToggleLink = useCallback(
    async (id: string, type: CatalogItemType, currentlyLinked: boolean) => {
      if (currentlyLinked) {
        // DESVINCULAR: Mostrar diálogo de confirmación con preview
        setUnlinkDialog({
          isOpen: true,
          isLoading: true,
          isUnlinking: false,
          itemId: id,
          itemType: type,
          preview: null,
        });

        try {
          const preview = await catalogApi.getUnlinkPreview(siteId, type, id);
          setUnlinkDialog((prev) => ({
            ...prev,
            isLoading: false,
            preview,
          }));
        } catch (error: any) {
          toast.error('Error', error.message || 'No se pudo obtener la información');
          setUnlinkDialog((prev) => ({ ...prev, isOpen: false }));
        }
      } else {
        // VINCULAR: Mostrar diálogo de confirmación con padres
        const itemName = getItemName(id, type);
        const parentsToLink = getUnlinkedParents(id, type);

        setLinkDialog({
          isOpen: true,
          isLinking: false,
          itemId: id,
          itemName,
          itemType: type,
          parentsToLink,
        });
      }
    },
    [siteId, getItemName, getUnlinkedParents]
  );

  // Confirmar vinculación
  const handleConfirmLink = useCallback(async () => {
    setLinkDialog((prev) => ({ ...prev, isLinking: true }));

    try {
      const result = await catalogApi.linkItem(siteId, linkDialog.itemType, linkDialog.itemId);
      toast.success('Vinculado', result.message);
      
      if (result.linkedParents && result.linkedParents.length > 0) {
        toast.info(
          'Vinculación en cascada',
          `También se vincularon: ${result.linkedParents.join(', ')}`
        );
      }
      
      onRefresh();
    } catch (error: any) {
      toast.error('Error', error.message || 'No se pudo vincular el elemento');
    } finally {
      setLinkDialog({
        isOpen: false,
        isLinking: false,
        itemId: '',
        itemName: '',
        itemType: 'specialty',
        parentsToLink: [],
      });
    }
  }, [siteId, linkDialog.itemType, linkDialog.itemId, onRefresh]);

  // Cerrar diálogo de link
  const handleCloseLinkDialog = useCallback(() => {
    if (!linkDialog.isLinking) {
      setLinkDialog({
        isOpen: false,
        isLinking: false,
        itemId: '',
        itemName: '',
        itemType: 'specialty',
        parentsToLink: [],
      });
    }
  }, [linkDialog.isLinking]);

  // Confirmar desvinculación
  const handleConfirmUnlink = useCallback(async () => {
    setUnlinkDialog((prev) => ({ ...prev, isUnlinking: true }));

    try {
      const result = await catalogApi.unlinkItem(
        siteId,
        unlinkDialog.itemType,
        unlinkDialog.itemId
      );
      toast.success('Desvinculado', result.message);
      
      if (result.unlinkedItems && result.unlinkedItems.length > 0) {
        toast.info(
          'Desvinculación en cascada',
          `Se desvincularon ${result.unlinkedItems.length} elementos adicionales`
        );
      }
      
      onRefresh();
    } catch (error: any) {
      toast.error('Error', error.message || 'No se pudo desvincular el elemento');
    } finally {
      setUnlinkDialog({
        isOpen: false,
        isLoading: false,
        isUnlinking: false,
        itemId: '',
        itemType: 'specialty',
        preview: null,
      });
    }
  }, [siteId, unlinkDialog.itemType, unlinkDialog.itemId, onRefresh]);

  // Cerrar diálogo de unlink
  const handleCloseUnlinkDialog = useCallback(() => {
    if (!unlinkDialog.isUnlinking) {
      setUnlinkDialog({
        isOpen: false,
        isLoading: false,
        isUnlinking: false,
        itemId: '',
        itemType: 'specialty',
        preview: null,
      });
    }
  }, [unlinkDialog.isUnlinking]);

  // Verificar si hay algo expandido
  const hasExpanded =
    Object.values(effectiveExpanded.specialties).some(Boolean) ||
    Object.values(effectiveExpanded.subspecialties).some(Boolean) ||
    Object.values(effectiveExpanded.treatmentGroups).some(Boolean);

  // Contar resultados
  const resultCount = filteredData.specialties.length;
  const isFiltering = searchQuery.trim().length > 0;

  return (
    <>
      {/* Search and Toolbar */}
      <div className="space-y-3 mb-4">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar especialidades, grupos, actos..."
            className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {isFiltering ? (
              <>
                <span className="font-medium text-gray-700">{resultCount}</span>
                {' '}resultado{resultCount !== 1 ? 's' : ''} encontrado{resultCount !== 1 ? 's' : ''}
              </>
            ) : (
              'Marca los elementos que deseas activar en este centro'
            )}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              disabled={filteredData.specialties.length === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronDown className="h-4 w-4" />
              Expandir todo
            </button>
            <button
              onClick={collapseAll}
              disabled={!hasExpanded}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
              Colapsar todo
            </button>
          </div>
        </div>
      </div>

      {/* Tree */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        {filteredData.specialties.length === 0 ? (
          <div className="p-8 text-center">
            {isFiltering ? (
              <>
                <p className="text-gray-500">No se encontraron resultados para "{searchQuery}"</p>
                <button
                  onClick={clearSearch}
                  className="mt-2 text-sm text-primary hover:underline"
                >
                  Limpiar búsqueda
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-500">No hay especialidades disponibles</p>
                <p className="text-sm text-gray-400 mt-1">
                  Crea especialidades desde Administración para empezar
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredData.specialties.map((specialty) => (
              <CatalogTreeItem
                key={specialty.id}
                id={specialty.id}
                code={specialty.code}
                name={specialty.name}
                type="specialty"
                isLinked={specialty.isLinked}
                isExpanded={effectiveExpanded.specialties[specialty.id] || false}
                isLoading={loadingItems.has(specialty.id)}
                hasChildren={specialty.subspecialties.length > 0}
                linkedCount={specialty.linkedSubspecialtiesCount}
                totalCount={specialty.totalSubspecialtiesCount}
                depth={0}
                onToggleExpand={() => toggleExpand('specialties', specialty.id)}
                onToggleLink={handleToggleLink}
                isHighlighted={filteredData.matchedIds.has(specialty.id)}
                searchQuery={searchQuery}
              >
                {/* Subespecialidades */}
                {specialty.subspecialties.map((subspecialty) => (
                  <CatalogTreeItem
                    key={subspecialty.id}
                    id={subspecialty.id}
                    code={subspecialty.code}
                    name={subspecialty.name}
                    type="subspecialty"
                    isLinked={subspecialty.isLinked}
                    isExpanded={effectiveExpanded.subspecialties[subspecialty.id] || false}
                    isLoading={loadingItems.has(subspecialty.id)}
                    hasChildren={subspecialty.treatmentGroups.length > 0}
                    linkedCount={subspecialty.linkedGroupsCount}
                    totalCount={subspecialty.totalGroupsCount}
                    depth={1}
                    onToggleExpand={() => toggleExpand('subspecialties', subspecialty.id)}
                    onToggleLink={handleToggleLink}
                    isHighlighted={filteredData.matchedIds.has(subspecialty.id)}
                    searchQuery={searchQuery}
                  >
                    {/* Grupos de tratamiento */}
                    {subspecialty.treatmentGroups.map((group) => (
                      <CatalogTreeItem
                        key={group.id}
                        id={group.id}
                        code={group.code}
                        name={group.name}
                        type="treatmentGroup"
                        isLinked={group.isLinked}
                        isExpanded={effectiveExpanded.treatmentGroups[group.id] || false}
                        isLoading={loadingItems.has(group.id)}
                        hasChildren={group.acts.length > 0}
                        linkedCount={group.linkedActsCount}
                        totalCount={group.totalActsCount}
                        depth={2}
                        onToggleExpand={() => toggleExpand('treatmentGroups', group.id)}
                        onToggleLink={handleToggleLink}
                        isHighlighted={filteredData.matchedIds.has(group.id)}
                        searchQuery={searchQuery}
                      >
                        {/* Actos médicos */}
                        {group.acts.map((act) => (
                          <CatalogTreeItem
                            key={act.id}
                            id={act.id}
                            code={act.code}
                            name={act.name}
                            type="medicalAct"
                            isLinked={act.isLinked}
                            isExpanded={false}
                            isLoading={loadingItems.has(act.id)}
                            hasChildren={false}
                            depth={3}
                            onToggleExpand={() => {}}
                            onToggleLink={handleToggleLink}
                            isHighlighted={filteredData.matchedIds.has(act.id)}
                            searchQuery={searchQuery}
                          />
                        ))}
                      </CatalogTreeItem>
                    ))}
                  </CatalogTreeItem>
                ))}
              </CatalogTreeItem>
            ))}
          </div>
        )}
      </div>

      {/* Link Preview Dialog */}
      <LinkPreviewDialog
        isOpen={linkDialog.isOpen}
        isLinking={linkDialog.isLinking}
        itemName={linkDialog.itemName}
        itemType={linkDialog.itemType}
        parentsToLink={linkDialog.parentsToLink}
        onClose={handleCloseLinkDialog}
        onConfirm={handleConfirmLink}
      />

      {/* Unlink Preview Dialog */}
      <UnlinkPreviewDialog
        isOpen={unlinkDialog.isOpen}
        isLoading={unlinkDialog.isLoading}
        isUnlinking={unlinkDialog.isUnlinking}
        preview={unlinkDialog.preview}
        onClose={handleCloseUnlinkDialog}
        onConfirm={handleConfirmUnlink}
      />
    </>
  );
}

export default CatalogTree;
